import { useState } from 'react';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { AirtimeContact, FundingWallet } from '@/types/portal';

interface AirtimeFormStepProps {
  favourites: AirtimeContact[];
  onManageFavourites: () => void;
  onPickContact: (contact: AirtimeContact) => void;
  ownNumber: boolean;
  onOwnNumber: (value: boolean) => void;
  ownLine: string;
  number: string;
  onNumber: (value: string) => void;
  amount: number;
  onAmount: (value: number) => void;
  presets: number[];
  minimum: number;
  currency: string;
  wallets: FundingWallet[];
  walletId: string;
  onWallet: (id: string) => void;
  fee: number;
  total: number;
  error: string;
  canManage: boolean;
  canContinue: boolean;
  onContinue: () => void;
}

/**
 * "Buy Airtime" form — choose who to top up (own line or another number), then
 * capture the amount with one-tap presets. Mirrors the mobile airtime UX on
 * desktop.
 */
export default function AirtimeFormStep({
  favourites,
  onManageFavourites,
  onPickContact,
  ownNumber,
  onOwnNumber,
  ownLine,
  number,
  onNumber,
  amount,
  onAmount,
  presets,
  minimum,
  currency,
  wallets,
  walletId,
  onWallet,
  fee,
  total,
  error,
  canManage,
  canContinue,
  onContinue,
}: AirtimeFormStepProps) {
  const [contactsHint, setContactsHint] = useState(false);
  const money = currency || 'KES';
  const wallet = wallets.find((item) => item.id === walletId) ?? null;
  const shownNumber = ownNumber ? ownLine : number;

  return (
    <div className="space-y-7">
      {/* Top up for */}
      <section>
        <h3 className="font-heading text-base font-semibold text-foreground-950">Top Up For</h3>
        <div className="mt-4 flex flex-wrap items-center gap-6">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="radio"
              name="airtime-target"
              checked={ownNumber}
              onChange={() => onOwnNumber(true)}
              className="h-4 w-4 cursor-pointer accent-primary-500"
            />
            Own Number
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="radio"
              name="airtime-target"
              checked={!ownNumber}
              onChange={() => onOwnNumber(false)}
              className="h-4 w-4 cursor-pointer accent-primary-500"
            />
            Other Number
          </label>
        </div>
      </section>

      {/* Favourites — only when topping up someone else */}
      {!ownNumber ? (
        <section>
          <h3 className="font-heading text-base font-semibold text-foreground-950">Favourites</h3>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-4">
            <li>
              <button
                type="button"
                onClick={onManageFavourites}
                className="flex w-[72px] cursor-pointer flex-col items-center gap-2 text-center"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-dashed border-background-300 bg-background-100 text-xl text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-700">
                  <i className="ri-add-line" />
                </span>
                <span className="text-xs text-foreground-500">Add</span>
              </button>
            </li>
            {favourites.map((contact) => (
              <li key={contact.id}>
                <button
                  type="button"
                  onClick={() => onPickContact(contact)}
                  className="flex w-[72px] cursor-pointer flex-col items-center gap-2 text-center"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-base font-semibold text-accent-800">
                    {contact.name.charAt(0)}
                  </span>
                  <span className="line-clamp-2 text-xs text-foreground-600">
                    {contact.name.split(' ')[0]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Phone number + amount */}
      <section className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="airtime-number" className="text-sm font-label text-foreground-700">
            Phone Number
          </label>
          <div className="relative mt-2">
            <input
              id="airtime-number"
              value={shownNumber}
              inputMode="numeric"
              readOnly={ownNumber}
              onChange={(event) => onNumber(event.target.value.replace(/\D/g, ''))}
              placeholder="Enter Phone Number"
              className={`h-12 w-full rounded-lg border border-background-300 bg-background-50 pl-4 pr-12 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none ${
                ownNumber ? 'cursor-not-allowed text-foreground-500' : ''
              }`}
            />
            {!ownNumber ? (
              <button
                type="button"
                aria-label="Search contacts"
                onClick={() => setContactsHint((value) => !value)}
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-lg text-primary-600 transition-colors hover:bg-primary-50"
              >
                <i className="ri-contacts-line" />
              </button>
            ) : null}
          </div>
          {contactsHint && !ownNumber ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
              <i className="ri-information-line" />
              Pick from your favourites above, or type any number to top up a new line.
            </p>
          ) : null}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="airtime-amount" className="text-sm font-label text-foreground-700">
            Enter Amount
          </label>
          <div className="relative mt-2">
            <input
              id="airtime-amount"
              type="number"
              min={0}
              value={Number.isNaN(amount) || amount === 0 ? '' : amount}
              onChange={(event) => onAmount(Number(event.target.value))}
              placeholder="0.00"
              className="h-12 w-full rounded-lg border border-background-300 bg-background-50 pl-4 pr-16 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
              {money === 'KES' ? 'Ksh' : money}
            </span>
          </div>
          <p className="mt-1.5 text-xs text-foreground-500">
            Minimum top up amount Ksh. {minimum}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {presets.map((preset) => {
              const isActive = amount === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => onAmount(preset)}
                  className={`cursor-pointer rounded-md border px-3.5 py-2 text-xs font-label transition-colors ${
                    isActive
                      ? 'border-primary-300 bg-primary-100 text-primary-800'
                      : 'border-background-200 bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  Ksh. {preset}
                </button>
              );
            })}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="airtime-from" className="text-sm font-label text-foreground-700">
            Send from
          </label>
          <select
            id="airtime-from"
            value={walletId}
            onChange={(event) => onWallet(event.target.value)}
            className="mt-2 h-12 w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          >
            <option value="">Select a wallet...</option>
            {wallets.map((item) => (
              <option key={item.id} value={item.id}>
                {item.currency} float · {formatMoney(item.balance, item.currency)}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Balance strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-500">
        <span>
          Balance:{' '}
          <span className="font-medium text-foreground-800">
            {wallet ? formatMoney(wallet.balance, wallet.currency) : '—'}
          </span>
        </span>
        <span>
          Transaction cost:{' '}
          <span className="font-medium text-foreground-800">
            {fee > 0 ? formatMoney(fee, money) : 'N/A'}
          </span>
        </span>
        <span>
          Total:{' '}
          <span className="font-medium text-foreground-950">{formatMoney(total, money)}</span>
        </span>
      </div>

      {!canManage ? (
        <p className="flex items-center gap-1.5 text-xs text-primary-700">
          <i className="ri-lock-line" />
          Your role can view payments but not send them.
        </p>
      ) : error ? (
        <p className="flex items-center gap-1.5 text-xs text-primary-700">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      ) : null}

      <Button
        icon="ri-sim-card-line"
        size="lg"
        onClick={onContinue}
        disabled={!canContinue}
        className="w-full sm:w-auto sm:min-w-[220px]"
      >
        Top Up
      </Button>
    </div>
  );
}